import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { Button, Container, SectionHeader, Tabs, TabsContent, TabsList, TabsTrigger } from "@athena/ui";
import { VideoAssetsDocument } from "@/graphql/media.generated";
import { DEFAULT_PAGE_SIZE } from "@/hooks/useWindowedRows";
import { BrandsPanel } from "./BrandsPanel";
import { VideosPanel } from "./VideosPanel";
import { useState } from "react";
import { VideoSelectorModal } from "../Courses/VideoSelectorModal";
import { Plus } from "lucide-react";
import { usePlatformAdmin } from "@/lib/usePlatformAdmin";

/**
 * One page for editing the platform's reference data, with a tab per kind.
 *
 * Brands and videos both had a hole — brands could only be added by a migration, and a
 * video could be uploaded with a bad filename and never renamed — and neither justifies a
 * screen of its own. A tab is cheaper to add than a route the next time something needs
 * editing.
 *
 * The active tab lives in the query string so a tab can be linked and survives a reload.
 */
const TABS = [
    "brands",
    "videos",
] as const;

type DataTab = (typeof TABS)[number];

const isTab = (value: string | null): value is DataTab => TABS.includes((value ?? "") as DataTab);

export function DataPage() {
    const { t } = useTranslation();
    const [
        params,
        setParams,
    ] = useSearchParams();
    const [
        videoModalOpen,
        setVideoModalOpen,
    ] = useState<boolean>(false);
    const active: DataTab = isTab(params.get("tab")) ? (params.get("tab") as DataTab) : "brands";

    const { isPlatformAdmin, organizationId, loading: meLoading } = usePlatformAdmin();

    // Same query VideosPanel runs — refetching here updates its cached result too, so the
    // new video shows up there whether or not that tab is currently mounted. The variables must
    // match VideosPanel's exactly or the two cache entries diverge and the refetch updates nothing.
    // That panel now reads through `useWindowedRows`, so the page size comes from there rather than
    // being written out again: an appending merge policy makes a mismatched `limit` worse than a
    // divergence, because this query's rows would land on top of the window.
    const { refetch: refetchVideoAssets } = useQuery(VideoAssetsDocument, {
        variables: { organizationId, limit: DEFAULT_PAGE_SIZE, offset: 0 },
        skip: meLoading || (!isPlatformAdmin && !organizationId),
    });

    return (
        <Container className="p-6">
            <SectionHeader
                eyebrow={t("admin.data.eyebrow")}
                title={t("admin.data.title")}
                description={t("admin.data.description")}
                actions={
                    active === "videos" && (
                        <Button size="sm" className="flex items-center gap-1.5" onClick={() => setVideoModalOpen(true)}>
                            <Plus className="h-3.5 w-3.5" />
                            {t("admin.lms.addVideo")}
                        </Button>
                    )
                }
            />
            <Tabs
                value={active}
                onValueChange={(value) => setParams({ tab: value }, { replace: true })}
                className="mt-6"
            >
                <TabsList>
                    <TabsTrigger value="brands">{t("admin.data.tabBrands")}</TabsTrigger>
                    <TabsTrigger value="videos">{t("admin.data.tabVideos")}</TabsTrigger>
                </TabsList>
                <TabsContent value="brands" className="mt-6">
                    <BrandsPanel />
                </TabsContent>
                <TabsContent value="videos" className="mt-6">
                    <VideosPanel />
                </TabsContent>
            </Tabs>
            <VideoSelectorModal
                open={videoModalOpen}
                onOpenChange={setVideoModalOpen}
                // The listing scope, matching the query above — undefined means "every
                // organization" for a platform admin. Passing their own membership instead scoped
                // the picker to one org while the page behind it still showed all of them, so an
                // admin with a membership saw fewer videos in the picker than in the grid.
                // The upload's owner is not this: `isPlatformAdmin` lets the server place it.
                organizationId={organizationId}
                isPlatformAdmin={isPlatformAdmin}
                onUpload={() => {
                    setVideoModalOpen(false);
                    void refetchVideoAssets();
                }}
            />
        </Container>
    );
}
